import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@folio/components/ui';
import { useAccessiblePayerCustomerIds } from '@folio/hooks/useAccessiblePayerCustomerIds';
import { usePlannedRecoveryTasks } from '@folio/hooks/usePlannedRecoveryTasks';
import { InvoiceDetailsDrawer } from './InvoiceDetailsDrawer';
import { InvoiceGroupRow, type InvoiceGroup } from './InvoiceGroupRow';
import { invoiceDocumentsOf } from './invoiceDocuments';
import { useQuery, useSuspenseQueries } from '@tanstack/react-query';
import type { Invoice } from 'korint-sdk';
import {
  configControllerGetPublicProductConfigQueryOptions,
  customerControllerGetCustomerQueryOptions,
  viewSpecificControllerListCustomerInvoicesQueryOptions,
} from 'korint-sdk';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePolicyOnDashboard } from '../../utils/usePolicyOnDashboard';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  DocumentViewer,
  DocumentViewerDocument,
  useDocumentViewer,
} from '@folio/components/features/document-viewer';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { customerDisplayName } from '@folio/utils/customerDisplayName';
import { groupBy } from 'lodash';
import { Text } from '@folio/components/ui/text';

function groupInvoicesByCustomer(
  invoicesByCustomer: Map<string, Invoice[]>,
): Map<string, InvoiceGroup[]> {
  const groupedInvoicesByCustomer = new Map<string, InvoiceGroup[]>();

  for (const [customerId, invoices] of invoicesByCustomer.entries()) {
    const groupsMap = new Map<
      string,
      {
        original?: Invoice;
        credit?: Invoice;
        replacement?: Invoice;
      }
    >();

    for (const invoice of invoices) {
      const groupId = invoice.replacedInvoiceId || invoice.creditedInvoiceId || invoice.id;

      if (!groupsMap.has(groupId)) {
        groupsMap.set(groupId, {});
      }

      const group = groupsMap.get(groupId)!;

      if (invoice.creditedInvoiceId) {
        group.credit = invoice;
      } else if (invoice.replacedInvoiceId) {
        group.replacement = invoice;
      } else {
        group.original = invoice;
      }
    }

    groupedInvoicesByCustomer.set(
      customerId,
      Array.from(groupsMap.values()).map(group => ({
        displayInvoice: group.replacement || group.original!,
        originalInvoice: group.replacement ? group.original : undefined,
        creditInvoice: group.credit,
      })),
    );
  }

  return groupedInvoicesByCustomer;
}

function groupByYearInvoicesByCustomer(
  invoiceGroupsByCustomer: Map<string, InvoiceGroup[]>,
): Map<string, [number, InvoiceGroup[]][]> {
  const groupeByYearInvoicesByCustomer = new Map<string, [number, InvoiceGroup[]][]>();

  for (const [customerId, invoiceGroups] of invoiceGroupsByCustomer.entries()) {
    const map = new Map<number, InvoiceGroup[]>();

    for (const group of invoiceGroups) {
      const year = new Date(group.displayInvoice.createdAt).getFullYear();
      if (!map.has(year)) map.set(year, []);
      map.get(year)!.push(group);
    }

    groupeByYearInvoicesByCustomer.set(
      customerId,
      Array.from(map.entries())
        .map(([year, groups]): [number, InvoiceGroup[]] => [
          year,
          groups.sort(
            (a, b) =>
              new Date(b.displayInvoice.period.startedAt).getTime() -
              new Date(a.displayInvoice.period.startedAt).getTime(),
          ),
        ])
        .sort(([a], [b]) => a - b),
    );
  }

  return groupeByYearInvoicesByCustomer;
}

function invoiceToDocumentViewerDocuments(
  invoice: Invoice,
  t: TFunction,
): DocumentViewerDocument[] {
  return invoiceDocumentsOf(invoice, t).map(({ id, signedUrl, fileName }) => ({
    id,
    name: fileName,
    signedUrl,
    createdAt: invoice.issuedAt,
    type: 'generated',
    category: 'INVOICE',
    metadata: {},
  }));
}

export function InvoicesModule() {
  const { t } = useTranslation();
  const customerInformations = usePolicyProductConfig('customerInformations');
  const { policy, workingBranch } = usePolicyOnDashboard();
  const [searchParams, setSearchParams] = useSearchParams();
  const payerCustomerIds = useAccessiblePayerCustomerIds(policy);
  const hasMultipleCustomers = payerCustomerIds.length > 1;
  const { recoveryTasks, refetch: refetchRecoveryTasks } = usePlannedRecoveryTasks();
  const retryTasksByInvoice = groupBy(
    recoveryTasks.filter(({ task }) => task.task === 'unpaid-retry'),
    ({ task }) => task.payload.invoiceId,
  );

  // Fetch invoices for all accessible customers in parallel
  const invoicesByCustomer = useSuspenseQueries({
    queries: payerCustomerIds.map(customerId => ({
      ...viewSpecificControllerListCustomerInvoicesQueryOptions(customerId, {
        branchId: workingBranch?.id,
      }),
    })),
  });

  const customers = useSuspenseQueries({
    queries: payerCustomerIds.map(customerId => ({
      ...customerControllerGetCustomerQueryOptions(customerId, {
        branchId: workingBranch?.id,
      }),
    })),
  });

  const customersRole = useMemo(() => {
    return new Map(customers.map(({ data }) => [data.id, data]));
  }, [customers]);

  const { data: publicProductConfig } = useQuery(
    configControllerGetPublicProductConfigQueryOptions(policy.productId),
  );
  const offlineEnabled =
    publicProductConfig?.payment?.allowedPaymentModes.includes('OFFLINE') ?? false;

  const invoiceGroupsByCustomer = useMemo(
    () =>
      groupInvoicesByCustomer(
        new Map(
          payerCustomerIds.map((customerId, index) => {
            return [customerId, invoicesByCustomer[index].data.data];
          }),
        ),
      ),
    [payerCustomerIds, invoicesByCustomer],
  );

  const invoiceGroupsByYearByCustomer = useMemo(
    () => groupByYearInvoicesByCustomer(invoiceGroupsByCustomer),
    [invoiceGroupsByCustomer],
  );

  const currentYear = new Date().getFullYear();

  const selectedInvoiceId = searchParams.get('invoiceId');
  const selectedInvoice = selectedInvoiceId
    ? Array.from(invoiceGroupsByCustomer)
        .flatMap(([customerId, groups]) => groups.map(group => ({ customerId, group })))
        .find(({ group }) =>
          [group.displayInvoice.id, group.originalInvoice?.id, group.creditInvoice?.id].includes(
            selectedInvoiceId,
          ),
        )
    : undefined;

  const handleOpenDetails = (group: InvoiceGroup) => {
    setSearchParams(previous => {
      const updated = new URLSearchParams(previous);
      updated.set('invoiceId', group.displayInvoice.id);
      return updated;
    });
  };

  const handleCloseDrawer = () => {
    setSearchParams(previous => {
      const updated = new URLSearchParams(previous);
      updated.delete('invoiceId');
      return updated;
    });
  };

  const viewableDocuments: DocumentViewerDocument[] = useMemo(() => {
    return invoicesByCustomer.flatMap(({ data }) =>
      data.data.flatMap(invoice => invoiceToDocumentViewerDocuments(invoice, t)),
    );
  }, [invoicesByCustomer, t]);

  const { selectedDocument, selectDocument, closeViewer } = useDocumentViewer(viewableDocuments);

  const handleViewInvoice = useCallback(
    (invoice: Invoice) => {
      const [firstDocument] = invoiceToDocumentViewerDocuments(invoice, t);
      if (firstDocument) {
        selectDocument(firstDocument);
      }
    },
    [selectDocument, t],
  );

  return (
    <>
      {payerCustomerIds.map((customerId, index) => {
        const { refetch } = invoicesByCustomer[index];
        const invoiceGroups = invoiceGroupsByCustomer.get(customerId) ?? [];
        const invoiceGroupsByYear = invoiceGroupsByYearByCustomer.get(customerId) ?? [];

        const customer = customersRole.get(customerId);

        return (
          <>
            <Card>
              <CardHeader>
                <CardTitle>
                  {t('invoices.module.title')}
                  {hasMultipleCustomers && (
                    <Text inline size="sm" weight="normal" tone="muted" className="ml-2">
                      (
                      {customerDisplayName(
                        customer ? [customer] : [],
                        customerInformations?.fieldsConfig,
                      ) || customerId}
                      )
                    </Text>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {invoiceGroups.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground">
                    {t('invoices.module.noInvoices')}
                  </div>
                ) : (
                  <Tabs
                    defaultValue={String(
                      invoiceGroupsByYear.find(([y]) => y === currentYear)?.[0] ??
                        invoiceGroupsByYear.at(-1)?.[0],
                    )}
                  >
                    <TabsList>
                      {invoiceGroupsByYear.map(([year]) => (
                        <TabsTrigger key={year} value={String(year)}>
                          {year}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                    {invoiceGroupsByYear.map(([year, groups]) => (
                      <TabsContent key={year} value={String(year)}>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>{t('invoices.module.createdAt')}</TableHead>
                              <TableHead>{t('invoices.module.period')}</TableHead>
                              <TableHead>{t('invoices.module.status')}</TableHead>
                              <TableHead>{t('invoices.module.amount')}</TableHead>
                              <TableHead />
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {groups.map(group => {
                              const nextRetry = retryTasksByInvoice[group.displayInvoice.id]?.[0];

                              return (
                                <InvoiceGroupRow
                                  key={group.displayInvoice.id}
                                  group={group}
                                  customerId={customerId}
                                  customerPaymentMode={customer?.paymentMode}
                                  offlineEnabled={offlineEnabled}
                                  nextRetryAt={nextRetry?.whenAt}
                                  onOpenDetails={handleOpenDetails}
                                  onRetrySuccess={() => {
                                    refetch();
                                    refetchRecoveryTasks?.();
                                  }}
                                  onViewInvoice={handleViewInvoice}
                                />
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TabsContent>
                    ))}
                  </Tabs>
                )}
              </CardContent>
            </Card>

            <DocumentViewer
              productId={policy.productId}
              document={selectedDocument}
              documents={viewableDocuments}
              onClose={closeViewer}
              onDocumentChange={selectDocument}
            />
          </>
        );
      })}
      {selectedInvoice && (
        <InvoiceDetailsDrawer
          group={selectedInvoice.group}
          customerId={selectedInvoice.customerId}
          plannedRetries={retryTasksByInvoice[selectedInvoice.group.displayInvoice.id] ?? []}
          open={true}
          onClose={handleCloseDrawer}
        />
      )}
    </>
  );
}
